import React, { useState } from 'react';
import { Zap, Trophy, ClipboardCheck, BookmarkPlus, CheckCheck, ChevronDown, ChevronUp, Share2, Download, ThumbsUp, ThumbsDown } from 'lucide-react';
import { api } from '../api';
import ShareModal from './ShareModal';
import FeedbackModal from './FeedbackModal'; 
import { toast } from 'sonner';

export default function GeneratedCards({ result, onSave, isSaving, isSaved, savedId, onExportPDF }) {
  const [revealAnswers, setRevealAnswers] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const [rating, setRating] = useState(result?.rating || null);
  const [feedbackType, setFeedbackType] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const { cards, topic, age, context_type } = result;

  // LÓGICA DE COMPATIBILIDAD:
  // Si el backend mandó la nueva estructura "clases" (array), la usamos.
  // Si mandó la vieja (sin "clases"), la envolvemos en un array de 1 elemento para que el `.map()` no se rompa.
  const clasesArray = cards?.clases ? cards.clases : [cards];

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const { data } = await api.post('/share', { 
        topic, 
        age, 
        cards,
        context_type: context_type || 'text'
      });
      const url = `${window.location.origin}/share/${data.share_id}`;
      setShareUrl(url);
      setShowShareModal(true);
    } catch {
      toast.error('Error al generar el enlace');
    } finally {
      setIsSharing(false);
    }
  };

  const handleExportClick = async () => {
    if (!onExportPDF) return;
    setIsExporting(true);
    try {
      await onExportPDF();
    } finally {
      setIsExporting(false);
    }
  };

  const handleFeedbackClick = (type) => {
    if (!savedId) {
      toast.info('Guardá la dinámica primero para registrar tu calificación', { duration: 2500 });
      return;
    }
    setFeedbackType(type);
    setShowFeedbackModal(true);
  };

  const submitFeedback = async (tags, comment) => {
    try {
      await api.patch(`/history/${savedId}/feedback`, { 
        rating: feedbackType,
        tags,
        comment
      });
      setRating(feedbackType); 
      // ¡Palabra IA erradicada por completo!
      toast.success('¡Gracias por ayudarnos a mejorar la plataforma!'); 
      setShowFeedbackModal(false);
    } catch {
      toast.error('Error al guardar tu opinión.');
    }
  };

  return (
    <div className="space-y-8">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-blue-400 uppercase tracking-[0.15em] font-semibold mb-0.5">Dinámicas generadas</p>
          <h2 className="text-lg font-bold text-white">{topic} · {age} años</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export PDF (AHORA COBRA CRÉDITO) */}
          <button
            data-testid="export-pdf-btn"
            onClick={handleExportClick}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 hover:border-slate-600 transition-all disabled:opacity-50"
          >
            {isExporting
              ? <div className="w-3.5 h-3.5 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
              : <Download size={14} strokeWidth={1.5} />
            }
            PDF (−1 crédito)
          </button>

          {/* Share */}
          <button
            data-testid="share-btn"
            onClick={handleShare}
            disabled={isSharing}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 hover:border-slate-600 transition-all disabled:opacity-50"
          >
            {isSharing
              ? <div className="w-3.5 h-3.5 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
              : <Share2 size={14} strokeWidth={1.5} />
            }
            Compartir
          </button>

          {/* Save */}
          <button
            data-testid="save-to-library-btn"
            onClick={onSave}
            disabled={isSaving || isSaved}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
              isSaved
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 cursor-default'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700 hover:border-slate-600'
            }`}
          >
            {isSaving ? (
              <div className="w-3.5 h-3.5 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
            ) : isSaved ? (
              <CheckCheck size={15} strokeWidth={1.5} />
            ) : (
              <BookmarkPlus size={15} strokeWidth={1.5} />
            )}
            {isSaved ? 'Guardado' : 'Guardar (−1 crédito)'}
          </button>
        </div>
      </div>

      {/* ITERAMOS SOBRE CADA CLASE GENERADA */}
      <div className="space-y-12">
        {clasesArray.map((clase, index) => (
          <div key={index} className="space-y-4">
            
            {/* Título de la Clase (Solo se muestra si hay más de 1 o si el backend mandó un título específico) */}
            {(clasesArray.length > 1 || clase.titulo_clase) && (
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-800"></div>
                <h3 className="text-slate-400 font-bold text-sm tracking-wider uppercase">
                  {clase.titulo_clase || `Sesión ${index + 1}`} {clase.duracion_minutos ? `(${clase.duracion_minutos} min)` : ''}
                </h3>
                <div className="h-px flex-1 bg-slate-800"></div>
              </div>
            )}

            {/* Cards grid para ESTA clase */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              
              {/* DESAFÍO DE APERTURA */}
              { (clase.desafio_apertura || clase.gancho) && (
                <div className="bg-slate-900 border border-blue-500/30 rounded-xl p-5 relative overflow-hidden hover:border-blue-400/50 transition-all animate-card-reveal stagger-1">
                  <div className="absolute top-0 left-0 w-24 h-24 bg-blue-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Zap size={14} className="text-blue-400" strokeWidth={1.5} />
                      </div>
                      <span className="text-xs text-blue-400/70 bg-blue-500/10 px-2 py-0.5 rounded-full">
                        {clase.desafio_apertura?.tiempo || clase.gancho?.tiempo || '2 min'} · Apertura
                      </span>
                    </div>
                    <h3 className="text-blue-400 font-bold text-sm mb-2">Desafío de Apertura</h3>
                    <p className="text-white text-sm font-semibold mb-2 leading-snug">
                      {clase.desafio_apertura?.titulo || clase.gancho?.titulo}
                    </p>
                    <p className="text-slate-300 text-xs leading-relaxed mb-4">
                      {clase.desafio_apertura?.pregunta_polemica || clase.gancho?.pregunta_polemica}
                    </p>
                    {(clase.desafio_apertura?.instrucciones || clase.gancho?.instrucciones) && (
                      <div className="border-t border-slate-800 pt-3">
                        <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Para el docente</p>
                        <p className="text-slate-400 text-xs leading-relaxed">
                          {clase.desafio_apertura?.instrucciones || clase.gancho?.instrucciones}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* RETO */}
              { clase.reto && (
                <div className="bg-slate-900 border border-violet-500/30 rounded-xl p-5 relative overflow-hidden hover:border-violet-400/50 transition-all animate-card-reveal stagger-2">
                  <div className="absolute top-0 left-0 w-24 h-24 bg-violet-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Trophy size={14} className="text-violet-400" strokeWidth={1.5} />
                      </div>
                      <span className="text-xs text-violet-400/70 bg-violet-500/10 px-2 py-0.5 rounded-full">{clase.reto?.tiempo || '10-15 min'} · Grupal</span>
                    </div>
                    <h3 className="text-violet-400 font-bold text-sm mb-2">El Reto Gamificado</h3>
                    <p className="text-white text-sm font-semibold mb-2 leading-snug">{clase.reto?.titulo}</p>
                    <p className="text-slate-300 text-xs leading-relaxed mb-4">{clase.reto?.descripcion}</p>
                    {clase.reto?.instrucciones && (
                      <div className="border-t border-slate-800 pt-3">
                        <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Paso a paso</p>
                        <p className="text-slate-400 text-xs leading-relaxed whitespace-pre-line">{clase.reto.instrucciones}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* EVALUACIÓN */}
              { clase.evaluacion && (
                <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-5 relative overflow-hidden hover:border-emerald-400/50 transition-all animate-card-reveal stagger-3">
                  <div className="absolute top-0 left-0 w-24 h-24 bg-emerald-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <ClipboardCheck size={14} className="text-emerald-400" strokeWidth={1.5} />
                      </div>
                      <span className="text-xs text-emerald-400/70 bg-emerald-500/10 px-2 py-0.5 rounded-full">5 min · Cierre</span>
                    </div>
                    <h3 className="text-emerald-400 font-bold text-sm mb-2">Micro-Evaluación</h3>
                    <p className="text-white text-sm font-semibold mb-3 leading-snug">{clase.evaluacion?.titulo}</p>

                    <div className="space-y-2.5">
                      {clase.evaluacion?.preguntas?.map((q, i) => (
                        <div key={i} className="bg-slate-800/60 rounded-lg p-2.5">
                          <p className="text-slate-200 text-xs font-medium mb-1.5">{i + 1}. {q.pregunta}</p>
                          <div className="space-y-1">
                            {q.opciones?.map((opt, j) => {
                              const isCorrect = revealAnswers && opt.startsWith(q.respuesta_correcta);
                              return (
                                <div
                                  key={j}
                                  className={`text-xs px-2 py-1 rounded transition-colors ${
                                    isCorrect ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-500'
                                  }`}
                                >
                                  {opt}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      data-testid={`reveal-answers-btn-${index}`}
                      onClick={() => setRevealAnswers(!revealAnswers)}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-emerald-400 border border-slate-700 hover:border-emerald-500/40 rounded-lg py-2 transition-all"
                    >
                      {revealAnswers ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      {revealAnswers ? 'Ocultar respuestas' : 'Revelar respuestas correctas'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Share Modal ORIGINAL */}
      {shareUrl && (
        <ShareModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={shareUrl}
        />
      )}

      {/* Quality Feedback */}
      <div className="flex items-center justify-center gap-3 pt-6">
        <span className="text-xs text-slate-600">¿Fue útil esta planificación?</span>
        <button
          data-testid="feedback-thumbs-up"
          onClick={() => handleFeedbackClick('up')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            rating === 'up'
              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40'
              : 'bg-slate-800 text-slate-500 border-slate-700 hover:border-emerald-500/30 hover:text-emerald-400'
          }`}
        >
          <ThumbsUp size={12} strokeWidth={1.5} />
          Sí
        </button>
        <button
          data-testid="feedback-thumbs-down"
          onClick={() => handleFeedbackClick('down')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            rating === 'down'
              ? 'bg-red-500/15 text-red-400 border-red-500/40'
              : 'bg-slate-800 text-slate-500 border-slate-700 hover:border-red-500/30 hover:text-red-400'
          }`}
        >
          <ThumbsDown size={12} strokeWidth={1.5} />
          No
        </button>
      </div>

      {/* NUEVO: MODAL DE FEEDBACK AVANZADO */}
      <FeedbackModal 
          open={showFeedbackModal} 
          onClose={() => setShowFeedbackModal(false)} 
          type={feedbackType} 
          onSubmit={submitFeedback} 
      />

    </div>
  );
}
